import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { StudioPremiumDeployReadiness } from "../../lib/studio/studioPremiumDemo";
import {
  createUnavailableStudioDeploymentSnapshot,
  evaluateStudioDeploymentState,
  loadStudioDeploymentSnapshot,
  type StudioDeploymentDecision,
  type StudioDeploymentStatusCardTone,
} from "../../lib/studio/studioDeploymentState";
import { buildStudioCanaryStateSummary } from "../../lib/studio/studioCanaryState";
import {
  getStudioPublishApprovalChecklistItems,
  type StudioWorkspaceApprovalSelectionKey,
  type StudioWorkspaceReadinessSelectionKey,
  type StudioWorkspaceState,
} from "../../lib/studio/studioWorkspaceState";

function summaryBlock(title: string, lines: string[]) {
  return (
    <section
      style={{
        borderRadius: 22,
        border: "1px solid rgba(54, 44, 35, 0.08)",
        background:
          "linear-gradient(180deg, rgba(251, 248, 243, 0.98), rgba(255, 255, 255, 0.98))",
        padding: "1rem",
      }}
    >
      <div
        style={{
          fontSize: "0.74rem",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "#85796e",
          fontWeight: 700,
        }}
      >
        {title}
      </div>
      <ul
        style={{
          margin: "0.7rem 0 0",
          paddingLeft: "1.1rem",
          color: "#4d453d",
          lineHeight: 1.7,
        }}
      >
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </section>
  );
}

function toneStyles(tone: StudioDeploymentStatusCardTone) {
  if (tone === "ready") {
    return {
      background: "rgba(44, 93, 67, 0.12)",
      color: "#214735",
      dot: "#2c5d43",
      border: "1px solid rgba(44, 93, 67, 0.16)",
    };
  }

  if (tone === "review") {
    return {
      background: "rgba(181, 141, 34, 0.14)",
      color: "#6d5210",
      dot: "#8f6e16",
      border: "1px solid rgba(181, 141, 34, 0.2)",
    };
  }

  return {
    background: "rgba(162, 62, 62, 0.12)",
    color: "#812d2d",
    dot: "#a23e3e",
    border: "1px solid rgba(162, 62, 62, 0.16)",
  };
}

function statePill(label: string, tone: StudioDeploymentStatusCardTone) {
  const styles = toneStyles(tone);

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.42rem",
        borderRadius: 999,
        padding: "0.3rem 0.64rem",
        background: styles.background,
        color: styles.color,
        fontSize: "0.74rem",
        fontWeight: 700,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          borderRadius: 999,
          background: styles.dot,
        }}
      />
      {label}
    </span>
  );
}

function statusCard(
  label: string,
  value: string,
  detail: string,
  tone: StudioDeploymentStatusCardTone
) {
  const styles = toneStyles(tone);

  return (
    <div
      key={`${label}_${value}`}
      style={{
        borderRadius: 22,
        border: styles.border,
        background:
          tone === "blocked"
            ? "linear-gradient(180deg, rgba(255, 247, 244, 0.96), rgba(255, 252, 248, 0.98))"
            : "linear-gradient(180deg, rgba(251, 248, 243, 0.98), rgba(255, 255, 255, 0.98))",
        padding: "1rem",
      }}
    >
      <div
        style={{
          fontSize: "0.72rem",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "#85796e",
          fontWeight: 700,
        }}
      >
        {label}
      </div>
      <div
        style={{
          marginTop: "0.42rem",
          display: "inline-flex",
          alignItems: "center",
          gap: "0.5rem",
          color: styles.color,
          fontWeight: 700,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 10,
            height: 10,
            borderRadius: 999,
            background: styles.dot,
          }}
        />
        {value}
      </div>
      <p style={{ margin: "0.45rem 0 0", color: "#5b534b", lineHeight: 1.55 }}>
        {detail}
      </p>
    </div>
  );
}

function blockerCategoryTone(category: string): StudioDeploymentStatusCardTone {
  if (category === "approval" || category === "launch_constraint") {
    return "review";
  }

  return "blocked";
}

function formatCheckedAt(checkedAt: string | null) {
  if (!checkedAt) {
    return "Awaiting current readiness snapshot";
  }

  const timestamp = new Date(checkedAt);
  if (Number.isNaN(timestamp.getTime())) {
    return "Current readiness snapshot loaded";
  }

  return `Snapshot checked ${new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(timestamp)}`;
}

function formatApprovalRecordedAt(recordedAt: string | null) {
  if (!recordedAt) {
    return "Internal sign-off not yet recorded";
  }

  const timestamp = new Date(recordedAt);
  if (Number.isNaN(timestamp.getTime())) {
    return "Internal sign-off recorded";
  }

  return `Internal sign-off recorded ${new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(timestamp)}`;
}

function stageTone(state: StudioDeploymentDecision["state"]): StudioDeploymentStatusCardTone {
  if (state === "canary_ready") return "ready";
  if (state === "preview_ready") return "review";
  return "blocked";
}

function flagPill(label: string, enabled: boolean) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.42rem",
        borderRadius: 999,
        padding: "0.28rem 0.6rem",
        background: enabled ? "rgba(44, 93, 67, 0.12)" : "rgba(162, 62, 62, 0.12)",
        color: enabled ? "#214735" : "#812d2d",
        fontSize: "0.73rem",
        fontWeight: 700,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          borderRadius: 999,
          background: enabled ? "#2c5d43" : "#a23e3e",
        }}
      />
      {label}
    </span>
  );
}

function launchControlReady(decision: StudioDeploymentDecision) {
  return !decision.blockers.some(
    (blocker) =>
      blocker.category === "launch_constraint" ||
      blocker.code === "launch_control_missing" ||
      blocker.code === "launch_control_unavailable"
  );
}

function rolloutManifestPresent(decision: StudioDeploymentDecision) {
  return !decision.blockers.some(
    (blocker) =>
      blocker.code === "rollout_snapshot_missing" ||
      blocker.code === "rollout_readiness_unavailable"
  );
}

export default function StudioDeployReadinessPanel(props: {
  readiness: StudioPremiumDeployReadiness;
  workspaceState: StudioWorkspaceState;
  onReadinessSelectionChange: (
    key: StudioWorkspaceReadinessSelectionKey,
    value: boolean
  ) => void;
  onApprovalSelectionChange: (
    key: StudioWorkspaceApprovalSelectionKey,
    value: boolean
  ) => void;
  onRecordApproval: () => void;
  onClearApproval: () => void;
  onDecisionChange?: (decision: StudioDeploymentDecision) => void;
}) {
  const [decision, setDecision] = useState<StudioDeploymentDecision | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    async function loadDecision() {
      setLoading(true);

      try {
        const snapshot = await loadStudioDeploymentSnapshot();
        if (!active) return;
        const nextDecision = evaluateStudioDeploymentState(snapshot);
        setDecision(nextDecision);
        props.onDecisionChange?.(nextDecision);
      } catch {
        if (!active) return;
        const fallbackDecision = evaluateStudioDeploymentState(
          createUnavailableStudioDeploymentSnapshot(
            "Studio deployment readiness could not be loaded."
          )
        );
        setDecision(fallbackDecision);
        props.onDecisionChange?.(fallbackDecision);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    void loadDecision();

    return () => {
      active = false;
    };
  }, []);

  const currentDecision =
    decision ||
    evaluateStudioDeploymentState(
      createUnavailableStudioDeploymentSnapshot(
        "Studio deployment readiness is still loading."
      )
    );

  const approvalChecklist = getStudioPublishApprovalChecklistItems(
    props.workspaceState
  );
  const approvalChecklistComplete = approvalChecklist.every(
    (item) => item.acknowledged
  );
  const approvalRecordedAt = props.workspaceState.deployment.approvalRecordedAt;

  const canarySummary = buildStudioCanaryStateSummary({
    deploymentState: currentDecision.state,
    launchControlReady: launchControlReady(currentDecision),
    rolloutManifestPresent: rolloutManifestPresent(currentDecision),
    rolloutEnabled:
      currentDecision.state === "canary_blocked" ||
      currentDecision.state === "canary_ready",
    canaryEligible: currentDecision.state === "canary_ready",
    internalApprovalRecorded: Boolean(approvalRecordedAt),
    approvalStatus:
      currentDecision.state === "canary_ready"
        ? "approved_for_a21_canary"
        : approvalChecklistComplete
          ? "approved_prep_only"
          : "pending_owner_approval",
    tenantCanaryAllowed: false,
    twilioRoutingUntouched: true,
    blockedReasons: currentDecision.blockers.map((blocker) => blocker.code),
    tenantScopeLabel: "Current Studio tenant rollout",
  });

  return (
    <section
      aria-labelledby="studio-deploy-readiness-heading"
      style={{
        marginTop: "1.2rem",
        borderRadius: 28,
        border: "1px solid rgba(162, 62, 62, 0.14)",
        background:
          "linear-gradient(180deg, rgba(255, 247, 244, 0.96), rgba(255, 252, 248, 0.98))",
        padding: "1.2rem",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "1rem",
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        <div style={{ maxWidth: 760 }}>
          <div
            style={{
              fontSize: "0.78rem",
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "#8f5b4d",
              fontWeight: 700,
            }}
          >
            Install-day review
          </div>
          <h3
            id="studio-deploy-readiness-heading"
            style={{
              margin: "0.45rem 0 0",
              fontSize: "clamp(1.55rem, 2.4vw, 2.1rem)",
              lineHeight: 1.08,
              fontFamily: "Georgia, 'Times New Roman', serif",
            }}
          >
            {props.readiness.title}
          </h3>
          <p style={{ margin: "0.75rem 0 0", color: "#5b534b", lineHeight: 1.7 }}>
            {props.readiness.summary}
          </p>
          <p style={{ margin: "0.65rem 0 0", color: "#5b534b", lineHeight: 1.7 }}>
            Launch control is a safety check, not a deploy button. It shows what is
            ready for review, what is still blocked, and what must stay in the
            separate rollout workflow.
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.55rem", flexWrap: "wrap" }}>
          {statePill(currentDecision.stateLabel, stageTone(currentDecision.state))}
          {statePill(
            loading ? "loading" : currentDecision.headline.toLowerCase(),
            stageTone(currentDecision.state)
          )}
        </div>
      </div>

      <section
        style={{
          marginTop: "1rem",
          borderRadius: 22,
          border: "1px solid rgba(54, 44, 35, 0.08)",
          background:
            "linear-gradient(180deg, rgba(251, 248, 243, 0.98), rgba(255, 255, 255, 0.98))",
          padding: "1rem",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "1rem",
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <div style={{ maxWidth: 760 }}>
            <div
              style={{
              fontSize: "0.74rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#85796e",
              fontWeight: 700,
            }}
          >
              Launch control
            </div>
            <div
              style={{
                marginTop: "0.45rem",
                display: "flex",
                gap: "0.7rem",
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              {statePill(currentDecision.headline, stageTone(currentDecision.state))}
              <span style={{ color: "#6b6259" }}>
                {formatCheckedAt(currentDecision.checkedAt)}
              </span>
            </div>
            <p style={{ margin: "0.8rem 0 0", color: "#5b534b", lineHeight: 1.7 }}>
              {loading
                ? "Checking the current preview, safety, and rollout snapshot."
                : currentDecision.summary}
            </p>
          </div>
        </div>

        <div
          style={{
            marginTop: "1rem",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "0.85rem",
          }}
        >
          {currentDecision.statusCards.map((card) =>
            statusCard(card.label, card.value, card.detail, card.tone)
          )}
        </div>
      </section>

         <section
        style={{
          marginTop: "1rem",
          borderRadius: 22,
          border: "1px solid rgba(41, 84, 103, 0.14)",
          background:
            "linear-gradient(180deg, rgba(240, 248, 250, 0.9), rgba(248, 251, 252, 0.98))",
          padding: "1rem",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "1rem",
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <div style={{ maxWidth: 760 }}>
            <div
              style={{
              fontSize: "0.74rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#5b7884",
              fontWeight: 700,
            }}
          >
              Separate rollout review
            </div>
            <div
              style={{
                marginTop: "0.45rem",
                display: "flex",
                gap: "0.7rem",
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              {statePill(canarySummary.headline, canarySummary.promotionBlocked ? "review" : "ready")}
              <span style={{ color: "#5b534b", lineHeight: 1.6 }}>
                {canarySummary.summary}
              </span>
            </div>
          </div>
        </div>

        <div
          style={{
            marginTop: "0.95rem",
            display: "flex",
            gap: "0.55rem",
            flexWrap: "wrap",
          }}
        >
          {canarySummary.flags.map((flag) => (
            <div key={flag.id}>{flagPill(flag.label, flag.enabled)}</div>
          ))}
        </div>

        <div
          style={{
            marginTop: "1rem",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "0.85rem",
          }}
        >
          {summaryBlock("Tenant restrictions", canarySummary.tenantRestrictions)}
          {summaryBlock("Promotion note", [canarySummary.promotionNote])}
        </div>
      </section>

      <div
        style={{
          marginTop: "1rem",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "0.85rem",
        }}
      >
        {summaryBlock("Draft persona", props.readiness.personaSummary)}
        {summaryBlock("Preview voice", props.readiness.voiceSummary)}
        {summaryBlock("Approved scope", props.readiness.capabilitySummary)}
        {summaryBlock("Routing truth", props.readiness.routingSummary)}
      </div>

      <div
        style={{
          marginTop: "1rem",
          display: "grid",
          gap: "1rem",
        }}
      >
        <section
          style={{
            borderRadius: 22,
            border: "1px solid rgba(41, 84, 103, 0.14)",
            background:
              "linear-gradient(180deg, rgba(240, 248, 250, 0.9), rgba(248, 251, 252, 0.98))",
            padding: "1rem",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "1rem",
              alignItems: "flex-start",
              flexWrap: "wrap",
            }}
          >
            <div style={{ maxWidth: 760 }}>
              <div
                style={{
                  fontSize: "0.74rem",
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "#5b7884",
                  fontWeight: 700,
                }}
              >
                Internal signoff checklist
              </div>
              <p style={{ margin: "0.6rem 0 0", color: "#39535f", lineHeight: 1.7 }}>
                Record internal signoff only after the preview-only posture,
                routing truth, current blockers, and separate rollout approval
                notes have all been reviewed. This records internal review only
                and never turns the system live.
              </p>
            </div>
            {statePill(
              approvalRecordedAt
                ? "internal signoff recorded"
                : approvalChecklistComplete
                  ? "ready for internal signoff"
                  : "awaiting signoff inputs",
              approvalRecordedAt
                ? "ready"
                : approvalChecklistComplete
                  ? "review"
                  : "blocked"
            )}
          </div>

          <div
            style={{
              marginTop: "0.95rem",
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) minmax(260px, 0.8fr)",
              gap: "1rem",
              alignItems: "start",
            }}
          >
            <div style={{ display: "grid", gap: "0.75rem" }}>
              {approvalChecklist.map((item) => (
                <label
                  key={item.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto minmax(0, 1fr)",
                    gap: "0.75rem",
                    alignItems: "start",
                    borderRadius: 18,
                    border: "1px solid rgba(41, 84, 103, 0.12)",
                    background: "rgba(255, 255, 255, 0.85)",
                    padding: "0.85rem 0.95rem",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={`Studio approval ${item.label.toLowerCase()}`}
                    checked={item.acknowledged}
                    onChange={(event) => {
                      if (item.source === "readiness" && item.readinessKey) {
                        props.onReadinessSelectionChange(
                          item.readinessKey,
                          event.target.checked
                        );
                        return;
                      }

                      if (item.source === "approval" && item.approvalKey) {
                        props.onApprovalSelectionChange(
                          item.approvalKey,
                          event.target.checked
                        );
                      }
                    }}
                    style={{ marginTop: "0.15rem" }}
                  />
                  <span>
                    <span
                      style={{
                        display: "block",
                        fontWeight: 700,
                        color: "#26221d",
                      }}
                    >
                      {item.label}
                    </span>
                    <span
                      style={{
                        display: "block",
                        marginTop: "0.3rem",
                        color: "#5b534b",
                        lineHeight: 1.55,
                      }}
                    >
                      {item.detail}
                    </span>
                  </span>
                </label>
              ))}
            </div>

            <div
              style={{
                borderRadius: 18,
                border: "1px solid rgba(54, 44, 35, 0.08)",
                background:
                  "linear-gradient(180deg, rgba(251, 248, 243, 0.98), rgba(255, 255, 255, 0.98))",
                padding: "0.95rem",
              }}
            >
              <div
                style={{
                  fontSize: "0.74rem",
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "#85796e",
                  fontWeight: 700,
                }}
              >
                Internal sign-off status
              </div>
              <p style={{ margin: "0.6rem 0 0", color: "#5b534b", lineHeight: 1.65 }}>
                {formatApprovalRecordedAt(approvalRecordedAt)}
              </p>
              <p style={{ margin: "0.6rem 0 0", color: "#5b534b", lineHeight: 1.65 }}>
                {approvalChecklist.filter((item) => item.acknowledged).length} of{" "}
                {approvalChecklist.length} required acknowledgements complete.
              </p>
              <p style={{ margin: "0.6rem 0 0", color: "#5b534b", lineHeight: 1.65 }}>
                Current rollout state stays {currentDecision.stateLabel}. Internal
                signoff does not bypass blockers, launch control, external canary
                approval, or the separate deployment workflow.
              </p>
              <p style={{ margin: "0.6rem 0 0", color: "#5b534b", lineHeight: 1.65 }}>
                Studio prepares the handoff package and internal review record.
                Live deployment remains outside this surface.
              </p>
              <div
                style={{
                  marginTop: "0.9rem",
                  display: "flex",
                  gap: "0.75rem",
                  flexWrap: "wrap",
                }}
              >
                <button
                  type="button"
                  aria-label="Record internal signoff"
                  disabled={!approvalChecklistComplete}
                  onClick={props.onRecordApproval}
                  style={{
                    borderRadius: 999,
                    padding: "0.78rem 1rem",
                    border: approvalChecklistComplete
                      ? "1px solid rgba(41, 84, 103, 0.18)"
                      : "1px solid rgba(181, 141, 34, 0.22)",
                    background: approvalChecklistComplete
                      ? "linear-gradient(135deg, #173847, #285466)"
                      : "rgba(181, 141, 34, 0.12)",
                    color: approvalChecklistComplete ? "#f7f4ee" : "#6d5210",
                    fontWeight: 700,
                    cursor: approvalChecklistComplete ? "pointer" : "not-allowed",
                  }}
                >
                  Record internal signoff
                </button>
                <button
                  type="button"
                  aria-label="Clear internal signoff"
                  onClick={props.onClearApproval}
                  disabled={!approvalRecordedAt}
                  style={{
                    borderRadius: 999,
                    padding: "0.78rem 1rem",
                    border: "1px solid rgba(162, 62, 62, 0.18)",
                    background: approvalRecordedAt
                      ? "rgba(162, 62, 62, 0.08)"
                      : "rgba(162, 62, 62, 0.04)",
                    color: approvalRecordedAt ? "#812d2d" : "#b08888",
                    fontWeight: 700,
                    cursor: approvalRecordedAt ? "pointer" : "not-allowed",
                  }}
                >
                  Clear internal signoff
                </button>
              </div>
            </div>
          </div>
        </section>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(280px, 0.8fr)",
            gap: "1rem",
            alignItems: "start",
          }}
        >
        <section
          style={{
            borderRadius: 22,
            border: "1px solid rgba(162, 62, 62, 0.14)",
            background: "rgba(255, 250, 248, 0.98)",
            padding: "1rem",
          }}
        >
          <div
            style={{
              fontSize: "0.74rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#8f5b4d",
              fontWeight: 700,
            }}
          >
            Blocking reasons
          </div>
          <div style={{ marginTop: "0.8rem", display: "grid", gap: "0.75rem" }}>
            {currentDecision.blockers.length === 0 ? (
              <div
                style={{
                  borderRadius: 18,
                  border: "1px solid rgba(44, 93, 67, 0.16)",
                  background: "rgba(244, 250, 246, 0.98)",
                  padding: "0.85rem 0.95rem",
                  color: "#214735",
                  lineHeight: 1.55,
                }}
              >
                No blocking reasons are currently open in the deployment snapshot.
              </div>
            ) : (
              currentDecision.blockers.map((blocker) => (
                <div
                  key={blocker.code}
                  style={{
                    borderRadius: 18,
                    border: toneStyles(blockerCategoryTone(blocker.category)).border,
                    background:
                      blockerCategoryTone(blocker.category) === "review"
                        ? "rgba(255, 249, 238, 0.98)"
                        : "rgba(255, 245, 243, 0.98)",
                    padding: "0.85rem 0.95rem",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "0.75rem",
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ fontWeight: 700, color: "#26221d" }}>
                      {blocker.label}
                    </div>
                    {statePill(
                      blocker.category.replaceAll("_", " "),
                      blockerCategoryTone(blocker.category)
                    )}
                  </div>
                  <p style={{ margin: "0.38rem 0 0", color: "#5b534b", lineHeight: 1.55 }}>
                    {blocker.detail}
                  </p>
                </div>
              ))
            )}
          </div>
        </section>

        <section
          style={{
            borderRadius: 22,
            border: "1px solid rgba(41, 84, 103, 0.14)",
            background:
              "linear-gradient(180deg, rgba(240, 248, 250, 0.9), rgba(248, 251, 252, 0.98))",
            padding: "1rem",
          }}
        >
          <div
            style={{
              fontSize: "0.74rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#5b7884",
              fontWeight: 700,
            }}
          >
            What must happen next
          </div>
          <ul
            style={{
              margin: "0.7rem 0 0",
              paddingLeft: "1.1rem",
              color: "#39535f",
              lineHeight: 1.7,
            }}
          >
            {currentDecision.nextActions.map((action) => (
              <li key={action}>{action}</li>
            ))}
          </ul>
          <p style={{ margin: "0.85rem 0 0", color: "#39535f", lineHeight: 1.7 }}>
            {props.readiness.suggestedNextStep}
          </p>
          <p style={{ margin: "0.85rem 0 0", color: "#39535f", lineHeight: 1.7 }}>
            Studio prepares the operator handoff, internal signoff package, and
            rollout review package. Promotion into live deployment remains
            outside this surface.
          </p>
          <div
            style={{
              marginTop: "0.95rem",
              borderRadius: 18,
              border: "1px solid rgba(181, 141, 34, 0.16)",
              background: "rgba(255, 249, 238, 0.92)",
              padding: "0.9rem 0.95rem",
            }}
          >
            <div
              style={{
                fontSize: "0.72rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#8f6e16",
                fontWeight: 700,
              }}
            >
              Rollback-safe fallback
            </div>
            <ul
              style={{
                margin: "0.65rem 0 0",
                paddingLeft: "1.1rem",
                color: "#5b534b",
                lineHeight: 1.65,
              }}
            >
              <li>
                Clear internal signoff if the current draft or blocker picture is no
                longer trusted.
              </li>
              <li>
                Reopen Preview &amp; Test Center to confirm the blocked state without
                widening authority.
              </li>
              <li>
                If install-day checks regress outside Studio, stop edits and follow
                the rollback plan in <code>docs/a18/ROLLBACK_PLAN.md</code>.
              </li>
            </ul>
          </div>
          <div
            style={{
              marginTop: "0.95rem",
              display: "flex",
              gap: "0.75rem",
              flexWrap: "wrap",
            }}
          >
            <Link
              to="/studio/preview?returnStep=deploy_readiness"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 999,
                padding: "0.78rem 1rem",
                textDecoration: "none",
                background: "linear-gradient(135deg, #173847, #285466)",
                color: "#f7f4ee",
                fontWeight: 700,
              }}
            >
              Reopen Preview & Test Center
            </Link>
            <button
              type="button"
              aria-label="Live clinic deployment blocked"
              disabled
              style={{
                borderRadius: 999,
                padding: "0.78rem 1rem",
                border: "1px solid rgba(162, 62, 62, 0.18)",
                background: "rgba(162, 62, 62, 0.12)",
                color: "#812d2d",
                fontWeight: 700,
                cursor: "not-allowed",
              }}
            >
              {props.readiness.gate.actionLabel}
            </button>
          </div>
        </section>
        </div>
      </div>
    </section>
  );
}
